import { type Button as PageButtonProps } from '@web/graphql-components'
import { Button as ChakraButton } from '@web/components/atoms/Button'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import { Box } from '@web/components/atoms/Layout'
import { useMediaQuery } from '@chakra-ui/react'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { DESKTOP_CONTAINER_QUERY, MOBILE_CONTAINER_QUERY, scrollToVisibleAnchor } from '../utils'
import { useState } from 'react'

export type ButtonProps = PageButtonProps & { onClick?: () => void }

export const getButtonColors = (color: string, textColor: string, outlineColor: string, variant: string) =>
  variant === 'outline'
    ? { borderColor: color, color }
    : { bgColor: color, color: textColor, borderColor: outlineColor }

const Button = ({ id, buttonText, target, url, buttonType, styles, mobileStyles, onClick }: ButtonProps) => {
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })
  const { font } = useBrandingsStore(({ buttonData }) => buttonData)
  const variant = buttonType === 'Secondary' ? 'outline' : 'solid'
  const bgColor = `var(--branding-button-${buttonType?.toLowerCase() ?? 'primary'}-bg-color)`
  const bgTextColor = `var(--branding-button-${buttonType?.toLowerCase() ?? 'primary'}-text-color)`
  const bgOutlineColor = `var(--branding-button-${buttonType?.toLowerCase() ?? 'primary'}-outline-color)`
  const hoverBgColor = `var(--branding-button-${buttonType?.toLowerCase() ?? 'primary'}-hover-bg-color)`
  const hoverBgTextColor = `var(--branding-button-${buttonType?.toLowerCase() ?? 'primary'}-hover-text-color)`
  const hoverBgOutlineColor = `var(--branding-button-${buttonType?.toLowerCase() ?? 'primary'}-hover-outline-color)`
  const colorStyles = isMobile ? mobileStyles : styles
  const backgroundColor = useColorWithOpacity(
    colorStyles?.backgroundColor,
    colorStyles?.backgroundColorOpacity,
    isMobile ? (styles?.backgroundColor ?? bgColor) : bgColor,
  )
  const textColor = useColorWithOpacity(
    colorStyles?.textColor,
    '1',
    isMobile ? (styles?.textColor ?? bgTextColor) : bgTextColor,
  )
  const outlineColor = useColorWithOpacity(
    colorStyles?.outlineColor ?? colorStyles?.backgroundColor,
    colorStyles?.outlineColorOpacity ?? colorStyles?.backgroundColorOpacity,
    isMobile ? (styles?.outlineColor ?? bgOutlineColor) : bgOutlineColor,
  )
  const hoverBackgroundColor = useColorWithOpacity(
    colorStyles?.hoverBackgroundColor,
    colorStyles?.hoverBackgroundColorOpacity,
    isMobile ? (styles?.hoverBackgroundColor ?? hoverBgColor) : hoverBgColor,
  )
  const hoverTextColor = useColorWithOpacity(
    colorStyles?.hoverTextColor,
    '1',
    isMobile ? (styles?.hoverTextColor ?? hoverBgTextColor) : hoverBgTextColor,
  )
  const hoverOutlineColor = useColorWithOpacity(
    colorStyles?.hoverOutlineColor ?? colorStyles?.hoverBackgroundColor,
    colorStyles?.hoverOutlineColorOpacity ?? colorStyles?.hoverBackgroundColorOpacity,
    isMobile ? (styles?.hoverOutlineColor ?? hoverBgOutlineColor) : hoverBgOutlineColor,
  )

  const buttonProps = {
    onClick:
      url === '#donation-form-element' || styles?.options?.anchorLink
        ? () => {
            if (url === '#donation-form-element') {
              scrollToVisibleAnchor('donation-form-element')
              return
            }

            if (styles?.options?.anchorLink) {
              scrollToVisibleAnchor(styles.options.anchorLink)
            }
          }
        : onClick,
    id,
    borderRadius: styles?.borderRadius ?? 'var(--branding-button-radius)',
    fontFamily: font?.font,
    alignSelf: styles?.alignSelf,
    cursor: 'pointer',
    ...getButtonColors(backgroundColor, textColor, outlineColor, variant),
    _hover: getButtonColors(hoverBackgroundColor, hoverTextColor, hoverOutlineColor, variant),
    css: {
      [MOBILE_CONTAINER_QUERY]: {
        fontSize: font.mobileFontSize ?? font.fontSize,
      },
      [DESKTOP_CONTAINER_QUERY]: {
        fontSize: font.fontSize,
      },
    },
  }

  const download = !!styles?.options?.download

  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    if (!url) {
      return
    }

    setIsDownloading(true)

    const response = await fetch(
      `/api/v1/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(buttonText)}`,
    )
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = url.split('/').pop() || 'download'
    a.click()
    URL.revokeObjectURL(blobUrl)

    setIsDownloading(false)
  }

  return (
    <Box
      display="grid"
      w="100%"
      css={{
        '@container page (min-width: 0px)': {
          '& > button, & > a': {
            padding: mobileStyles?.padding ?? '8px 16px',
          },
        },
        '@container page (min-width: 901px)': {
          '& > button, & > a': {
            padding: styles?.padding ?? '8px 16px',
          },
        },
      }}
    >
      {url !== '#donation-form-element' && !styles?.options?.anchorLink && !onClick ? (
        download ? (
          <ChakraButton
            {...buttonProps}
            variant={variant}
            buttonAppearance="unstyled"
            onClick={handleDownload}
            loading={isDownloading}
          >
            {buttonText}
          </ChakraButton>
        ) : (
          <ChakraButton {...buttonProps} buttonAppearance="unstyled" variant={variant} asChild={true}>
            <a
              href={url?.startsWith('http') || url?.startsWith('mailto:') ? url : `https://${url}`}
              target={target ?? '_self'}
            >
              {buttonText}
            </a>
          </ChakraButton>
        )
      ) : (
        <ChakraButton {...buttonProps} variant={variant} buttonAppearance="unstyled">
          {buttonText}
        </ChakraButton>
      )}
    </Box>
  )
}

export default Button
